import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, CheckCircle2, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const StaffAssistedIntake = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [caseNumber, setCaseNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [formData, setFormData] = useState({
    staff_name: '',
    staff_email: '',
    staff_church: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: 'IL',
    zip_code: '',
    request_types: [],
    details: '',
    entry_type: 'staff'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (type) => {
    setFormData(prev => ({
      ...prev,
      request_types: prev.request_types.includes(type)
        ? prev.request_types.filter(t => t !== type)
        : [...prev.request_types, type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.staff_name || !formData.staff_email || !formData.staff_church ||
        !formData.first_name || !formData.last_name || !formData.phone || 
        !formData.address || !formData.city || !formData.zip_code ||
        formData.request_types.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/intake/submit`, {
        ...formData,
        email: formData.email || null,
        recaptcha_token: recaptchaToken
      });
      
      setCaseNumber(response.data.case_number);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Submission failed';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="w-full max-w-2xl">
          <div className="glass-panel p-8 md:p-12 animate-fade-in text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-full mb-6 border border-emerald-500/30">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Request Submitted
            </h1>
            
            <p className="text-lg text-slate-300 mb-6">
              The request has been successfully submitted. SVdP members will follow up within 24-48 hours.
            </p>
            
            <div className="inline-block bg-white/10 backdrop-blur-md px-8 py-5 rounded-2xl border border-white/10 mb-8" data-testid="success-message">
              <p className="text-sm text-slate-400 mb-2">Case Number</p>
              <p className="text-4xl font-bold text-[#0072BC]" data-testid="case-number">{caseNumber}</p>
            </div>

            <div className="mb-8">
              <p className="text-slate-400">God bless you,</p>
              <p className="font-semibold text-white">SVdP Mary Mother of God</p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full font-medium transition-colors"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      <div className="container mx-auto max-w-3xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10">
              <img 
                src="/images/svdp-logo.jpg" 
                alt="SVdP Chicago" 
                className="h-16 w-auto"
                data-testid="svdp-logo"
              />
            </div>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full mb-4 border border-emerald-500/30">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Staff Entry</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Parish Staff Entry
          </h1>
          <p className="text-lg text-slate-400">Submit request on behalf of someone in need</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-panel p-6 md:p-8 animate-fade-in delay-100" data-testid="staff-intake-form">
          {/* Staff Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6 pb-3 border-b border-emerald-500/30 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-emerald-400" />
              </div>
              Staff Information
            </h2>

            <div className="grid gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Your Name *</label>
                <input
                  type="text"
                  name="staff_name"
                  value={formData.staff_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all outline-none"
                  required
                  data-testid="staff-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Your Email *</label>
                <input
                  type="email"
                  name="staff_email"
                  value={formData.staff_email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all outline-none"
                  required
                  data-testid="staff-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Church *</label>
                <select
                  name="staff_church"
                  value={formData.staff_church}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all outline-none appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                  required
                  data-testid="staff-church-select"
                >
                  <option value="" className="bg-slate-800">Select Church</option>
                  <option value="Saint Ita" className="bg-slate-800">Saint Ita</option>
                  <option value="Saint Gregory" className="bg-slate-800">Saint Gregory</option>
                  <option value="Saint Thomas" className="bg-slate-800">Saint Thomas</option>
                </select>
              </div>
            </div>
          </div>

          {/* Person in Need Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6 pb-3 border-b border-white/10" style={{ fontFamily: 'Playfair Display, serif' }}>
              Person in Need
            </h2>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="first-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="last-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Street Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="address-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="city-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                    required
                    data-testid="state-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                    required
                    data-testid="zip-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Nature of Need */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6 pb-3 border-b border-white/10" style={{ fontFamily: 'Playfair Display, serif' }}>
              Nature of Need
            </h2>
            
            <p className="text-slate-400 mb-4 text-sm">Select all that apply *</p>

            <div className="grid md:grid-cols-2 gap-3" data-testid="nature-of-need">
              {[
                { value: 'rent', label: 'Rent' },
                { value: 'utilities', label: 'Utilities' },
                { value: 'medication', label: 'Medication' },
                { value: 'ids', label: 'IDs / Documents' },
                { value: 'transportation', label: 'Transportation' },
                { value: 'food', label: 'Food' },
                { value: 'clothing', label: 'Clothing' },
                { value: 'other', label: 'Other' }
              ].map(type => (
                <label 
                  key={type.value} 
                  className={`flex items-center space-x-3 p-4 rounded-xl cursor-pointer transition-all border ${
                    formData.request_types.includes(type.value)
                      ? 'bg-[#0072BC]/20 border-[#0072BC]/50 text-white'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.request_types.includes(type.value)}
                    onChange={() => handleCheckboxChange(type.value)}
                    className="w-5 h-5 rounded bg-white/10 border-white/20 text-[#0072BC] focus:ring-[#0072BC] focus:ring-offset-0"
                    data-testid={`checkbox-${type.value}`}
                  />
                  <span className="font-medium">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-300 mb-2">Additional Details</label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none min-h-[120px] resize-y"
              placeholder="Any additional information about the situation..."
              data-testid="details-textarea"
            />
          </div>

          {/* reCAPTCHA and Submit */}
          <div className="text-center">
            {RECAPTCHA_SITE_KEY && (
              <div className="flex justify-center mb-6">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10">
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token || '')}
                    onExpired={() => setRecaptchaToken('')}
                    theme="dark"
                    data-testid="recaptcha"
                  />
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || (RECAPTCHA_SITE_KEY && !recaptchaToken)}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-10 py-4 font-medium text-lg transition-all shadow-lg shadow-emerald-900/30 hover:shadow-emerald-900/50 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffAssistedIntake;
